import { createContext, Dispatch, ReactNode, SetStateAction, useCallback, useContext, useState } from 'react';

export type FormType = 'product' | 'master' | 'house-consignment';

interface IDeclarationContext {
    form?: FormType;
    setForm?: Dispatch<SetStateAction<FormType>>;
    cards?: CardData[];
    registerCard?: (cardData: CardData) => void;
    unregisterCard?: (id: string) => void;
    clearCards?: () => void;
}

export interface CardData {
    /**
     * Identifies the card instance, not the card type. Two instances may share
     * a title and form - a card rendered both on its own and inside a multiple
     * items card, for example - and each has to be tracked separately so that
     * one unmounting does not deregister the other.
     */
    id: string;
    title: string;
    form: FormType;
    visible?: boolean;
    section?: boolean;
    required?: boolean;
}

interface Props {
    value?: IDeclarationContext;
    children: ReactNode;
}

export const DeclarationContext = createContext<IDeclarationContext>({});
export const useDeclarationContext = () => useContext(DeclarationContext);

export const DeclarationContextProvider = (props: Props) => {
    const [form, setForm] = useState<FormType>('master');
    const [cards, setCards] = useState<CardData[]>([]);

    const registerCard = useCallback((cardData: CardData) => {
        setCards((prev) => {
            const index = prev.findIndex((card) => card.id === cardData.id);
            if (index === -1) return [...prev, cardData];
            // Updated in place so the navigation keeps the order the cards render in.
            const next = [...prev];
            next[index] = cardData;
            return next;
        });
    }, []);

    const unregisterCard = useCallback((id: string) => {
        setCards((prev) => prev.filter((card) => card.id !== id));
    }, []);

    const clearCards = useCallback(() => setCards([]), []);

    return (
        <DeclarationContext.Provider
            value={{ form, setForm, cards, registerCard, unregisterCard, clearCards, ...props.value }}
        >
            {props.children}
        </DeclarationContext.Provider>
    );
};
